import { useEffect, useRef, useState } from 'react';
import { PRIZE_TABLE, type ScratchersState } from '../../engine/games/scratchers';
import { money, withCommas } from '../../format';
import { Controls, Stage, StatusLine, type TableProps } from './Frame';

const TOP_PRIZE = Math.max(...PRIZE_TABLE.map(([m]) => m));
/** How much foil has to come off before the rest flakes away by itself. */
const CLEAR_AT = 0.45;
/** A tap (no rubbing) dissolves the foil after this long. */
const TAP_CLEAR_MS = 380;
/** A rub that stopped short: the rest flakes off after this long. */
const RUB_CLEAR_MS = 520;
const SPOTS = [0, 1, 2, 3, 4, 5];

/**
 * Five ticket designs (user, 2026-09-27: variety, picked at random per
 * ticket). Only the look changes; the game is the same.
 */
const DESIGNS = [
  { name: 'Lucky 7s', tag: 'Cash' },
  { name: 'Gold Rush', tag: 'Bonanza' },
  { name: 'Cherry Bomb', tag: 'Jackpot' },
  { name: 'Deep Sea', tag: 'Treasure' },
  { name: 'Four Leaf', tag: 'Fortune' },
] as const;

function hash(key: string): number {
  let h = 2166136261;
  for (const c of key) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  return h;
}

/** A short, stable serial number for a ticket, printed like a real one. */
function serial(key: string): string {
  const d = String(hash(key)).padStart(10, '0');
  return `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7, 10)}`;
}

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

function paintFoil(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const { width: w, height: h } = canvas;
  ctx.globalCompositeOperation = 'source-over';
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, '#9da3aa');
  g.addColorStop(0.35, '#e9ecef');
  g.addColorStop(0.5, '#b4b9bf');
  g.addColorStop(0.7, '#f4f5f7');
  g.addColorStop(1, '#8e949b');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // Fine grain, so it reads as foil rather than paint.
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  for (let i = 0; i < (w * h) / 90; i++) ctx.fillRect(Math.random() * w, Math.random() * h, 1, 1);
  ctx.fillStyle = 'rgba(60,64,70,0.55)';
  ctx.font = `700 ${Math.round(h * 0.16)}px Karla, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('SCRATCH', w / 2, h / 2);
}

/** What the ticket needs from one spot's foil to rub it from a swipe that
 *  may cross several spots. */
interface FoilApi {
  /** Rub along a segment (client coordinates). True if it crossed this spot. */
  rub(x0: number, y0: number, x1: number, y1: number): boolean;
  /** The finger lifted after touching this spot (moved: how far it rubbed here). */
  release(moved: number): void;
}

/** The silver coating over one spot. The ticket drives the rubbing (so one
 *  swipe can cross every spot); the keyboard scratches a spot outright. */
function Foil({ index, register, onStart, onCleared, disabled }: { index: number; register: (i: number, api: FoilApi | null) => void; onStart: () => void; onCleared: () => void; disabled: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dust = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  const goneRef = useRef(false);
  const rubs = useRef(0);
  const [gone, setGone] = useState(false);
  // The latest callbacks, for the API the ticket holds on to.
  const live = useRef({ onStart, onCleared, disabled });
  live.current = { onStart, onCleared, disabled };

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    c.width = Math.max(1, Math.round(r.width));
    c.height = Math.max(1, Math.round(r.height));
    paintFoil(c);
  }, []);

  const finish = () => {
    if (goneRef.current) return;
    goneRef.current = true;
    setGone(true);
    // Fade the last of the foil, then drop it.
    setTimeout(() => live.current.onCleared(), reducedMotion() ? 0 : 280);
  };
  const start = () => {
    if (started.current) return;
    started.current = true;
    live.current.onStart();
  };

  useEffect(() => {
    const clearedShare = (c: HTMLCanvasElement) => {
      const ctx = c.getContext('2d');
      if (!ctx) return 1;
      const { data } = ctx.getImageData(0, 0, c.width, c.height);
      let clear = 0;
      let n = 0;
      for (let i = 3; i < data.length; i += 4 * 12) {
        n++;
        if (data[i]! < 40) clear++;
      }
      return n ? clear / n : 1;
    };
    const spray = (x: number, y: number) => {
      const box = dust.current;
      if (!box || reducedMotion() || box.childElementCount > 24) return;
      const p = document.createElement('span');
      p.className = 'foil-dust';
      p.style.left = `${x}px`;
      p.style.top = `${y}px`;
      p.style.setProperty('--dx', `${(Math.random() - 0.5) * 30}px`);
      box.appendChild(p);
      setTimeout(() => p.remove(), 650);
    };
    const api: FoilApi = {
      rub(x0, y0, x1, y1) {
        const c = ref.current;
        if (!c || goneRef.current || live.current.disabled) return false;
        const r = c.getBoundingClientRect();
        const pad = Math.max(7, r.width * 0.13);
        // Did the stroke come near this spot at all?
        const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) / 4));
        let hit = false;
        for (let k = 0; k <= steps && !hit; k++) {
          const x = x0 + ((x1 - x0) * k) / steps;
          const y = y0 + ((y1 - y0) * k) / steps;
          hit = x > r.left - pad && x < r.right + pad && y > r.top - pad && y < r.bottom + pad;
        }
        if (!hit) return false;
        start();
        const sx = c.width / r.width;
        const sy = c.height / r.height;
        const ctx = c.getContext('2d');
        if (ctx) {
          ctx.globalCompositeOperation = 'destination-out';
          ctx.lineCap = 'round';
          ctx.lineWidth = Math.max(14, c.width * 0.26);
          ctx.beginPath();
          ctx.moveTo((x0 - r.left) * sx, (y0 - r.top) * sy);
          ctx.lineTo((x1 - r.left) * sx, (y1 - r.top) * sy);
          ctx.stroke();
        }
        if (Math.random() < 0.5) spray(x1 - r.left, y1 - r.top);
        if (++rubs.current % 5 === 0 && clearedShare(c) >= CLEAR_AT) finish();
        return true;
      },
      release(moved) {
        if (goneRef.current) return;
        // A tap, or a rub that stopped short: the rest flakes off.
        setTimeout(finish, moved < 6 ? TAP_CLEAR_MS : RUB_CLEAR_MS);
      },
    };
    register(index, api);
    return () => register(index, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Keyboard: Enter/Space on the spot scratches it outright.
  const key = (e: React.KeyboardEvent) => {
    if (disabled || gone || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    start();
    setTimeout(finish, TAP_CLEAR_MS);
  };

  return (
    <div className={`foil${gone ? ' gone' : ''}`}>
      <canvas ref={ref} className="foil-canvas" role="button" tabIndex={disabled || gone ? -1 : 0} aria-label="Scratch this spot" onKeyDown={key} />
      <div ref={dust} className="foil-dust-box" aria-hidden="true" />
    </div>
  );
}

export function ScratchersTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.scratchers?.state as ScratchersState | undefined;
  const ticket = state?.ticket ?? null;
  const contents = ticket ? `${ticket.spots.join(',')}:${ticket.price}` : 'none';
  const design = hash(`${contents}#design`) % DESIGNS.length;
  // Each purchase gets its own key for the scratch state: two tickets in a
  // row can have the very same spots, and keyed on contents alone the second
  // inherited the first's scratched foil (found in a Codex review). A new
  // ticket is a new object with nothing revealed.
  const bought = useRef<{ ticket: typeof ticket; n: number }>({ ticket, n: 0 });
  if (ticket && ticket !== bought.current.ticket) {
    if (bought.current.ticket && ticket.revealed.every((r) => !r)) bought.current.n++;
    bought.current.ticket = ticket;
  }
  const key = ticket ? `${bought.current.n}:${contents}` : 'none';

  /**
   * The engine scratches a ticket's six spots in a fixed order, but their
   * values are already a random shuffle. So whichever spot you scratch is
   * given the next value in the engine's order (by swapping which engine
   * spot each on-screen spot shows); the odds are exactly the same as
   * scratching in order. One swipe can scratch several spots in the same
   * moment, so this is kept in a ref and updated at once, not on the next
   * render.
   */
  const map = useRef<{ key: string; order: number[]; issued: Set<number> }>({ key: '', order: SPOTS, issued: new Set() });
  if (map.current.key !== key) {
    map.current = { key, order: [...SPOTS], issued: new Set(ticket ? SPOTS.filter((e) => ticket.revealed[e]) : []) };
  }
  const [, redraw] = useState(0);
  const order = map.current.order;

  // Which on-screen spots have lost their foil (a web-only, visual state).
  const [cleared, setCleared] = useState<{ key: string; spots: boolean[] }>({ key: '', spots: [] });
  // A ticket seen for the first time (a new one, or one reloaded mid-play)
  // starts with foil only on the spots the engine hasn't revealed yet.
  const fresh = SPOTS.map((d) => !!ticket?.revealed[order[d]!]);
  const clearedSpots = cleared.key === key ? cleared.spots : fresh;
  const done = !!ticket && ticket.revealed.every((r) => r);
  useEffect(() => {
    if (cleared.key !== key) setCleared({ key, spots: fresh });
    // "Scratch all" reveals every spot in the engine at once: take all the
    // foil off too, or the result stays hidden (found in a Codex review).
    else if (done && map.current.issued.size < SPOTS.length) {
      map.current.issued = new Set(SPOTS);
      setCleared({ key, spots: SPOTS.map(() => true) });
    }
  });
  const inPlay = !!ticket && !done;
  const foilOn = (d: number) => !clearedSpots[d];
  const shownAll = done && SPOTS.every((d) => !foilOn(d));

  const scratch = (display: number) => {
    const m = map.current;
    if (!ticket || snapshot.anim || m.key !== key) return;
    const engineSpot = m.order[display]!;
    if (m.issued.has(engineSpot)) return;
    // The engine reveals its spots in order: the next one not yet asked for.
    const next = SPOTS.find((e) => !m.issued.has(e));
    if (next === undefined) return;
    const j = m.order.indexOf(next);
    [m.order[display], m.order[j]] = [m.order[j]!, m.order[display]!];
    m.issued.add(next);
    redraw((n) => n + 1);
    actions.tableOption('scratch');
  };
  const clear = (display: number) =>
    setCleared((c) => {
      const spots = c.key === key ? [...c.spots] : Array<boolean>(6).fill(false);
      spots[display] = true;
      return { key, spots };
    });

  // The ticket rubs: one finger can cross every spot in a single swipe.
  const foils = useRef(new Map<number, FoilApi>());
  const register = (i: number, api: FoilApi | null) => {
    if (api) foils.current.set(i, api);
    else foils.current.delete(i);
  };
  const stroke = useRef<{ x: number; y: number; moved: Map<number, number> } | null>(null);
  const rubAll = (x: number, y: number) => {
    const s = stroke.current;
    if (!s) return;
    const d = Math.hypot(x - s.x, y - s.y);
    for (const [i, api] of foils.current) if (api.rub(s.x, s.y, x, y)) s.moved.set(i, (s.moved.get(i) ?? 0) + d);
    s.x = x;
    s.y = y;
  };
  const down = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!ticket || snapshot.anim || (!inPlay && !SPOTS.some(foilOn))) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* fine without capture */
    }
    stroke.current = { x: e.clientX, y: e.clientY, moved: new Map() };
    rubAll(e.clientX, e.clientY);
  };
  const up = () => {
    const s = stroke.current;
    stroke.current = null;
    if (!s) return;
    for (const [i, moved] of s.moved) foils.current.get(i)?.release(moved);
  };

  const price = ticket?.price ?? run.tables.scratchers?.bet ?? 0;
  const returned = done ? ticket.prize * ticket.price : 0;
  const net = done ? returned - ticket.price : 0;
  const result = !shownAll
    ? undefined
    : ticket.prize === 0
      ? `No match (−${money(ticket.price)})`
      : net > 0
        ? `Winner! Pays ${money(returned)} (+${money(net)})`
        : `Match: your ${money(ticket.price)} back`;
  const frame = snapshot.anim?.frames[snapshot.anim.index];

  return (
    <>
      <Stage className="scratch-stage">
        <div className={`lotto-ticket design-${design}`}>
          <div className="lotto-head">
            <div className="lotto-title">
              <span className="lotto-name">{DESIGNS[design]!.name}</span>
              <span className="lotto-cash">{DESIGNS[design]!.tag}</span>
            </div>
            <span className="lotto-price">{money(price || 1)}</span>
          </div>
          <div className="lotto-rule">Match 3 like amounts, win that amount!</div>
          <div className="lotto-play" onPointerDown={down} onPointerMove={(e) => rubAll(e.clientX, e.clientY)} onPointerUp={up} onPointerCancel={up}>
            {SPOTS.map((d) => {
              if (!ticket) return <div key={d} className="spot blank"><span className="spot-blank-mark">?</span></div>;
              const e = order[d]!;
              const open = ticket.revealed[e];
              const value = ticket.spots[e]!;
              const win = shownAll && ticket.prize > 0 && value === ticket.prize;
              return (
                <div key={`${key}-${d}`} className={`spot${open ? ' open' : ''}${win ? ' win' : ''}`}>
                  <span className="spot-value">{open ? money(value * ticket.price) : ''}</span>
                  {foilOn(d) && <Foil index={d} register={register} onStart={() => scratch(d)} onCleared={() => clear(d)} disabled={!!snapshot.anim || (!inPlay && !open)} />}
                </div>
              );
            })}
          </div>
          <div className="lotto-foot">
            <span className="lotto-note">{!ticket ? 'Buy a ticket to play.' : inPlay ? 'Rub with your finger: one swipe can scratch them all.' : `Top prize ${TOP_PRIZE}× the ticket.`}</span>
            <span className="lotto-serial">
              <span className="barcode" aria-hidden="true" />
              No. {serial(key)}
            </span>
          </div>
          <div className="lotto-fine">Void if torn, altered or illegible · Prizes paid at the cage</div>
        </div>
        {frame && (
          <div className="sheet" onClick={actions.skip}>
            <pre>{withCommas(frame)}</pre>
          </div>
        )}
        <StatusLine snapshot={snapshot} result={result} tone={net > 0 ? 'win' : shownAll && ticket.prize === 0 ? 'loss' : ''} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['scratch']} />
    </>
  );
}
